/**
 * Live transcode status polling for a Commons file (Feature #6).
 *
 * After clip2commons finishes uploading the original WebM, Commons enqueues
 * one transcode job per resolution preset (240p, 480p, 1080p, ...). These
 * jobs run on backend workers and finish over the following minutes.
 *
 * The MediaWiki TimedMediaHandler exposes `prop=transcodestatus` for any file
 * page. Each entry has timestamps for: queued, started, succeeded, errored.
 * We poll once every 30 seconds and translate the result into a friendly
 * status table - so the user can see which renditions are ready.
 */

import { COMMONS_API_URL } from "./config"
import { getAccessToken, refreshAccessToken } from "./oauth"
import type { TranscodeEntry, TranscodeJobStatus, TranscodeStatusResult } from "./types"

const apiUrl = COMMONS_API_URL + "?crossorigin=&format=json&maxlag=5"

function authHeaders(): Record<string, string> {
  const token = getAccessToken()
  return token ? { Authorization: "Bearer " + token } : {}
}

async function authFetch(url: string, init?: RequestInit): Promise<Response> {
  const headers = { ...(init?.headers || {}), ...authHeaders() }
  let resp = await fetch(url, { ...init, headers })
  if (resp.status === 401) {
    const newToken = await refreshAccessToken()
    if (newToken) {
      const retryHeaders = { ...(init?.headers || {}), Authorization: "Bearer " + newToken }
      resp = await fetch(url, { ...init, headers: retryHeaders })
    }
  }
  return resp
}

/**
 * Convert a MediaWiki MW-format timestamp (e.g. "20240615123045") or ISO 8601
 * ("2024-06-15T12:30:45Z") into milliseconds since epoch. Returns NaN for
 * empty / unparseable input.
 */
function parseMwTimestamp(value: unknown): number {
  if (typeof value !== "string" || !value) return NaN
  // ISO 8601 - let Date handle it
  if (value.includes("-") || value.includes("T")) {
    const t = Date.parse(value)
    return Number.isFinite(t) ? t : NaN
  }
  // MW compact format: YYYYMMDDHHMMSS
  if (/^\d{14}$/.test(value)) {
    const iso = `${value.slice(0, 4)}-${value.slice(4, 6)}-${value.slice(6, 8)}T` +
                `${value.slice(8, 10)}:${value.slice(10, 12)}:${value.slice(12, 14)}Z`
    const t = Date.parse(iso)
    return Number.isFinite(t) ? t : NaN
  }
  return NaN
}

interface RawTranscodeRow {
  time_addjob?: string | null
  time_startwork?: string | null
  time_success?: string | null
  time_error?: string | null
  final_bitrate?: number | string | null
  error_message?: string | null
}

function deriveStatus(row: RawTranscodeRow): TranscodeJobStatus {
  if (row.time_success) return "completed"
  if (row.time_error) return "failed"
  if (row.time_startwork) return "transcoding"
  return "queued"
}

function deriveElapsedSeconds(row: RawTranscodeRow): number | null {
  const start = parseMwTimestamp(row.time_startwork) || parseMwTimestamp(row.time_addjob)
  if (!Number.isFinite(start)) return null
  const endRaw = row.time_success || row.time_error
  const end = endRaw ? parseMwTimestamp(endRaw) : Date.now()
  if (!Number.isFinite(end)) return null
  const ms = end - start
  if (ms < 0) return null
  return Math.round(ms / 1000)
}

/**
 * Fetch transcode status for a Commons file.
 *
 * The filename should be the bare title ("Foo.webm"), no "File:" prefix.
 * If the file has no transcodes yet (very fresh upload), returns an empty
 * entries list with allDone=false so the caller keeps polling.
 */
export async function getTranscodeStatus(filename: string): Promise<TranscodeStatusResult> {
  const fileTitle = filename.startsWith("File:") ? filename : "File:" + filename
  const url = apiUrl + "&action=query&prop=transcodestatus&titles=" + encodeURIComponent(fileTitle)

  const resp = await authFetch(url)
  if (!resp.ok) throw new Error("Transcode status fetch failed: HTTP " + resp.status)

  const json = await resp.json()
  if (json.error) {
    throw new Error("Transcode status fetch failed: " + (json.error.info || json.error.code))
  }

  const pages = json?.query?.pages || {}
  const entries: TranscodeEntry[] = []
  let foundPage = false
  for (const id of Object.keys(pages)) {
    foundPage = true
    const page = pages[id]
    const transcodestatus = page?.transcodestatus
    if (!transcodestatus || typeof transcodestatus !== "object") continue
    for (const key of Object.keys(transcodestatus)) {
      const row = transcodestatus[key] as RawTranscodeRow
      if (!row || typeof row !== "object") continue
      const finalBitrateRaw = row.final_bitrate
      const finalBitrate =
        typeof finalBitrateRaw === "number" ? finalBitrateRaw :
        typeof finalBitrateRaw === "string" ? Number(finalBitrateRaw) || 0 :
        0
      entries.push({
        key,
        status: deriveStatus(row),
        elapsedSeconds: deriveElapsedSeconds(row),
        finalBitrate,
        error: typeof row.error_message === "string" ? row.error_message : null
      })
    }
  }

  entries.sort((a, b) => a.key.localeCompare(b.key))

  // If the page couldn't be found at all, don't claim "allDone" - the file may
  // not have propagated yet. Return empty so the caller keeps polling.
  const allDone = foundPage && entries.length > 0 &&
    entries.every(e => e.status === "completed" || e.status === "failed")

  return { entries, allDone }
}
